import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type Theme = 'dark' | 'light'

/** §8.0 charts — a mutable object the provider reassigns on mode change; read through getters. */
export const PALETTE = ['#12355b', '#a87e27', '#4f6d8f', '#8a95a5', '#6f95bd', '#3f7d74', '#c9ab63', '#586577']

const DARK = {
  accent: '#7cb3ff', pos: '#34d399', neg: '#f87171', warn: '#f59e0b', gold: '#d6a84e',
  grid: '#1e293b', axis: '#334155', text: '#94a3b8', textStrong: '#e2e8f0',
  tooltipBg: '#0b1120', tooltipText: '#e8edf4',
}
const LIGHT = {
  ...DARK,
  accent: '#12355b', pos: '#157347', neg: '#b42318', warn: '#b54708', gold: '#a87e27',
  grid: '#e4e7ec', axis: '#cfd4dc', text: '#667085', textStrong: '#0b1b33',
}

export const COLORS: typeof DARK = { ...DARK }

export function applyChartColors(theme: Theme) {
  Object.assign(COLORS, theme === 'light' ? LIGHT : DARK)
}

const ThemeContext = createContext<{ theme: Theme; toggle: () => void }>({ theme: 'dark', toggle: () => {} })

export function ThemeProvider({ children }: { children: ReactNode }) {
  // light is the default; dark is the flip (the spec's design system had it the other way round)
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return localStorage.getItem('theme') === 'dark' ? 'dark' : 'light'
    } catch {
      return 'light'
    }
  })
  useEffect(() => {
    if (theme === 'light') document.documentElement.setAttribute('data-theme', 'light')
    else document.documentElement.removeAttribute('data-theme')
    applyChartColors(theme)
    try {
      localStorage.setItem('theme', theme)
    } catch {
      /* ignore */
    }
  }, [theme])
  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), [])
  const value = useMemo(() => ({ theme, toggle }), [theme, toggle])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => useContext(ThemeContext)
